import { useState, useEffect } from "react";
import { Plus, Trash2, GripVertical, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function CategoriesManager({ onChanged }) {
  const [cats, setCats] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState({}); // category_id -> name
  const [dragIdx, setDragIdx] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/categories");
      setCats(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (newName.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post("/admin/categories", { name: newName.trim() });
      setNewName("");
      toast.success("Catégorie ajoutée");
      load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const rename = async (c) => {
    const name = (editing[c.category_id] ?? c.name).trim();
    if (!name || name === c.name) return;
    try {
      await api.patch(`/admin/categories/${c.category_id}`, { name });
      toast.success("Catégorie renommée (produits associés mis à jour)");
      load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/admin/categories/${c.category_id}`);
      toast.success("Catégorie supprimée");
      load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const onDrop = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const next = [...cats];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setCats(next);
    setDragIdx(null);
    try {
      await api.put("/admin/categories/reorder", { ordered_ids: next.filter((c) => c.category_id).map((c) => c.category_id) });
      toast.success("Ordre enregistré");
    } catch { toast.error("Réordonnancement impossible"); }
  };

  return (
    <div className="max-w-2xl rounded-xl border border-slate-200 bg-white p-6" data-testid="categories-manager">
      <h3 className="font-display text-base font-semibold text-ciel">Catégories du catalogue</h3>
      <p className="mt-1 text-xs text-slate-500">Glissez-déposez pour réordonner — l'ordre est utilisé dans les filtres de la boutique.</p>

      <div className="mt-5 flex gap-2">
        <Input data-testid="category-new-name" placeholder="Nouvelle catégorie…" value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button data-testid="category-add" onClick={add} disabled={busy} className="rounded-full bg-ciel text-white hover:bg-sky-700">
          <Plus size={14} className="mr-1" /> Ajouter
        </Button>
      </div>

      <ul className="mt-5 space-y-2">
        {cats.map((c, i) => (
          <li key={c.category_id || c.name}
            draggable={!!c.category_id}
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 transition-shadow duration-200 hover:shadow-sm"
            data-testid={`category-row-${i}`}>
            <GripVertical size={15} className="cursor-grab text-slate-300" />
            <Input
              data-testid={`category-name-${i}`}
              value={editing[c.category_id] ?? c.name}
              onChange={(e) => setEditing({ ...editing, [c.category_id]: e.target.value })}
              className="h-8 flex-1 border-transparent bg-transparent text-sm font-medium text-slate-700 focus:border-ciel"
              disabled={!c.category_id}
            />
            <span className="rounded-full bg-slate-100 px-2.5 py-1 font-mono text-[10px] font-semibold text-slate-500" data-testid={`category-count-${i}`}>
              {c.produits ?? 0} produit(s)
            </span>
            {c.category_id && (
              <>
                <button onClick={() => rename(c)} data-testid={`category-save-${i}`} title="Enregistrer le nom"
                  className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:border-emerald-400 hover:text-emerald-600"><Check size={13} /></button>
                <button onClick={() => remove(c)} data-testid={`category-delete-${i}`} title="Supprimer"
                  className="rounded-lg border border-red-200 p-1.5 text-red-500 hover:bg-red-50"><Trash2 size={13} /></button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
